import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { User, Shield, School, Database, Calendar, Check, Sparkles, Edit2, Save, X, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressBar } from '../ui/Progress';
import { UserStats } from '../../types';
import { AccountRole } from '../../types/accounts';
import { GroupId, StartingPeriod, STARTING_PERIODS } from '../../config/systemConfig';
import { resolvePhaseByDate, getAutoPhaseLabel } from '../../utils/dateBasedPlacement';
import { XP_PER_LEVEL } from '../../config/constants';
interface ProfileSectionProps {
  stats: UserStats;
  displayName: string;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  manualPeriod?: StartingPeriod | null;
  onUpdateName?: (newName: string) => void;
  accountRole?: AccountRole;
  isViewingLearner?: boolean; // NEW: To distinguish between own profile and viewed learner
}
export function ProfileSection({
  stats,
  displayName,
  selectedGroup,
  startingPeriod,
  onSetStartingPeriod,
  manualPeriod,
  onUpdateName,
  accountRole = 'learner',
  isViewingLearner = false
}: ProfileSectionProps) {
  const progressToNextLevel = stats.xp % XP_PER_LEVEL / XP_PER_LEVEL * 100;
  const xpToNextLevel = XP_PER_LEVEL - stats.xp % XP_PER_LEVEL;
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayName);
  useEffect(() => {
    setEditedName(displayName);
  }, [displayName]);
  const handleSaveName = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayName && onUpdateName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    } else {
      setEditedName(displayName);
      setIsEditingName(false);
    }
  };
  const handleCancelEdit = () => {
    setEditedName(displayName);
    setIsEditingName(false);
  };
  // Role display label
  const roleLabel = (() => {
    switch (accountRole) {
      case 'learner':
        return 'Leerling';
      case 'parent':
        return 'Ouder';
      case 'teacher':
        return 'Docent';
      case 'admin':
        return 'Beheerder';
      default:
        return 'Leerling';
    }
  })();
  // Determine if we should show learning controls (XP, Level, Period)
  // Show if: Role is learner OR (Role is parent/teacher AND viewing a learner)
  const showLearningControls = accountRole === 'learner' || isViewingLearner;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="show" data-id="element-5016">
      {/* Banner for Parent/Teacher viewing Learner */}
      {isViewingLearner && <motion.div variants={itemVariants} data-id="element-5017">
          <Card className="p-4 bg-emerald-50 border-emerald-200 border-2" data-id="element-5018">
            <div className="flex items-center gap-3 text-emerald-800" data-id="element-5019">
              <Edit2 className="w-5 h-5" data-id="element-5020" />
              <p className="font-bold" data-id="element-5021">
                Je bekijkt en bewerkt nu de instellingen van{' '}
                <span className="underline" data-id="element-5022">{displayName}</span>.
              </p>
            </div>
          </Card>
        </motion.div>}

      {/* Banner for Parent/Teacher NOT viewing Learner */}
      {!showLearningControls && <motion.div variants={itemVariants} data-id="element-5023">
          <Card className="p-6 bg-indigo-50 border-indigo-200 border-2" data-id="element-5024">
            <div className="flex items-start gap-4" data-id="element-5025">
              <div className="bg-white p-2 rounded-full shadow-sm" data-id="element-5026">
                <User className="w-6 h-6 text-indigo-600" data-id="element-5027" />
              </div>
              <div data-id="element-5028">
                <h3 className="font-bold text-indigo-900 text-lg mb-1" data-id="element-5029">
                  Jouw Profiel
                </h3>
                <p className="text-indigo-700 mb-2" data-id="element-5030">
                  Je bent ingelogd als <strong data-id="element-5031">{roleLabel}</strong>. Hier kun je
                  je eigen naam aanpassen.
                </p>
                <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium" data-id="element-5032">
                  <AlertCircle className="w-4 h-4" data-id="element-5033" />
                  Selecteer een leerling in het portaal om hun instellingen te
                  wijzigen.
                </div>
              </div>
            </div>
          </Card>
        </motion.div>}

      {/* Main Profile Card */}
      <motion.div variants={itemVariants} data-id="element-5034">
        <Card className="p-10 md:p-12" data-id="element-5035">
          <div className="flex flex-col sm:flex-row items-center gap-8" data-id="element-5036">
            {/* Avatar */}
            <div className="relative flex-shrink-0" data-id="element-5037">
              <div className={`
                w-28 h-28 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg shadow-indigo-200/50
                ${showLearningControls ? 'bg-gradient-to-br from-indigo-400 to-indigo-600' : 'bg-gradient-to-br from-slate-400 to-slate-600'}
              `} data-id="element-5038">
                {displayName.charAt(0).toUpperCase()}
              </div>
              {showLearningControls && <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm" data-id="element-5039">
                  <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-5040">
                    Lvl {stats.level}
                  </div>
                </div>}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left" data-id="element-5041">
              {/* Name with Edit */}
              {isEditingName ? <div className="flex items-center gap-3 mb-3" data-id="element-5042">
                  <input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEdit();
              }} className="text-3xl font-bold text-slate-900 border-2 border-indigo-300 rounded-lg px-4 py-2 focus:outline-none focus:border-indigo-500 min-h-[56px]" autoFocus maxLength={30} data-id="element-5043" />
                  <button onClick={handleSaveName} className="p-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-emerald-300" title="Opslaan" data-id="element-5044">
                    <Save className="w-5 h-5" data-id="element-5045" />
                  </button>
                  <button onClick={handleCancelEdit} className="p-3 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-slate-300" title="Annuleren" data-id="element-5046">
                    <X className="w-5 h-5" data-id="element-5047" />
                  </button>
                </div> : <div className="flex items-center gap-4 mb-3 justify-center sm:justify-start" data-id="element-5048">
                  <h2 className="text-3xl font-black text-slate-900" data-id="element-5049">
                    {displayName}
                  </h2>
                  {onUpdateName && <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors min-h-[40px] min-w-[40px] focus:outline-none focus:ring-2 focus:ring-indigo-300" title="Naam wijzigen" data-id="element-5050">
                      <Edit2 className="w-5 h-5" data-id="element-5051" />
                    </button>}
                </div>}

              <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3" data-id="element-5052">
                {showLearningControls && <Badge variant="info" icon={<School className="w-4 h-4" data-id="element-5054" />} size="lg" data-id="element-5053">
                    Groep {selectedGroup}
                  </Badge>}
                <Badge variant="default" icon={<Shield className="w-4 h-4" data-id="element-5056" />} size="lg" data-id="element-5055">
                  {roleLabel}
                </Badge>
              </div>

              {/* XP Progress - Only for Learners */}
              {showLearningControls && <div className="mt-6 max-w-xs" data-id="element-5057">
                  <div className="flex justify-between text-sm text-slate-600 mb-2 font-semibold" data-id="element-5058">
                    <span data-id="element-5059">Level {stats.level}</span>
                    <span data-id="element-5060">
                      {stats.xp} XP • nog {xpToNextLevel} tot level{' '}
                      {stats.level + 1}
                    </span>
                  </div>
                  <ProgressBar value={progressToNextLevel} height={8} color="bg-indigo-500" data-id="element-5061" />
                </div>}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Starting Period Selector - Only for Learners */}
      {showLearningControls && <motion.div variants={itemVariants} data-id="element-5062">
          <Card className="p-8" data-id="element-5063">
            <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-xl" data-id="element-5064">
              <Calendar className="w-6 h-6 text-indigo-500" data-id="element-5065" />
              Wanneer ben je ingestapt?
            </h3>
            <p className="text-base text-slate-600 mb-6 font-semibold" data-id="element-5066">
              Standaard bepalen we dit automatisch op basis van de datum. Je
              kunt dit hieronder aanpassen.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-5067">
              {/* Auto Option */}
              <button onClick={() => onSetStartingPeriod(null)} className={`
                  relative p-5 rounded-xl border-2 transition-all text-center flex flex-col items-center justify-center min-h-[140px]
                  ${!manualPeriod ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}
                `} data-id="element-5068">
                {!manualPeriod && <div className="absolute top-3 right-3" data-id="element-5069">
                    <Check className="w-5 h-5 text-indigo-600" data-id="element-5070" />
                  </div>}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${!manualPeriod ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-100 text-slate-400'}`} data-id="element-5071">
                  <Sparkles className="w-6 h-6" data-id="element-5072" />
                </div>
                <div className={`font-bold text-base ${!manualPeriod ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-5073">
                  Automatisch
                </div>
                <div className="text-xs text-slate-500 mt-2" data-id="element-5074">
                  {getAutoPhaseLabel()}
                </div>
              </button>

              {/* Manual Options */}
              {STARTING_PERIODS.map(period => {
            const isActive = manualPeriod === period.id;
            return <button key={period.id} onClick={() => onSetStartingPeriod(period.id)} className={`
                      relative p-5 rounded-xl border-2 transition-all text-center min-h-[140px]
                      ${isActive ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}
                    `} data-id="element-5075">
                    {isActive && <div className="absolute top-3 right-3" data-id="element-5076">
                        <Check className="w-5 h-5 text-indigo-600" data-id="element-5077" />
                      </div>}
                    <div className={`text-3xl mb-3 ${isActive ? '' : 'grayscale opacity-60'}`} data-id="element-5078">
                      {period.id === 'B' ? '🌱' : period.id === 'M' ? '☀️' : '🎓'}
                    </div>
                    <div className={`font-bold text-base ${isActive ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-5079">
                      {period.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-5080">
                      {period.description}
                    </div>
                  </button>;
          })}
            </div>

            <div className="mt-6 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-5081">
              <p className="text-sm text-slate-700 flex items-center gap-2 font-semibold" data-id="element-5082">
                <span className="font-black text-indigo-600" data-id="element-5083">
                  Huidige start:
                </span>
                Niveau {startingPeriod}
                {selectedGroup}
                {manualPeriod ? ' (Handmatig ingesteld)' : ' (Automatisch bepaald)'}
              </p>
            </div>
          </Card>
        </motion.div>}

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-5084">
        <motion.div variants={itemVariants} data-id="element-5085">
          <Card className="p-8 h-full" data-id="element-5086">
            <h3 className="font-black text-slate-900 mb-6 flex items-center gap-3 text-xl" data-id="element-5087">
              <User className="w-6 h-6 text-slate-400" data-id="element-5088" />
              Persoonlijke Gegevens
            </h3>
            <div className="space-y-4" data-id="element-5089">
              {[{
              label: 'Naam',
              value: displayName
            }, {
              label: 'Rol',
              value: roleLabel
            }, {
              label: 'Schooljaar',
              value: (() => {
                const now = new Date();
                const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
                return `${year} – ${year + 1}`;
              })()
            }].map(row => <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-5090">
                  <span className="text-base text-slate-600 font-semibold" data-id="element-5091">
                    {row.label}
                  </span>
                  <span className="text-base font-bold text-slate-900" data-id="element-5092">
                    {row.value}
                  </span>
                </div>)}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-5093">
          <Card className="p-8 h-full" data-id="element-5094">
            <h3 className="font-black text-slate-900 mb-6 flex items-center gap-3 text-xl" data-id="element-5095">
              <Database className="w-6 h-6 text-slate-400" data-id="element-5096" />
              Opslag &amp; Data
            </h3>
            <div className="space-y-4" data-id="element-5097">
              {[{
              label: 'Opslagtype',
              value: 'Lokaal (browser)'
            }, ...(showLearningControls ? [{
              label: 'Huidige groep',
              value: `Groep ${selectedGroup}`
            }, {
              label: 'Startpunt',
              value: `${startingPeriod}${selectedGroup}`
            }, {
              label: 'Modus',
              value: manualPeriod ? 'Handmatig' : 'Auto'
            }] : [])].map(row => <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-5098">
                  <span className="text-base text-slate-600 font-semibold" data-id="element-5099">
                    {row.label}
                  </span>
                  <span className="text-base font-bold text-slate-900" data-id="element-5100">
                    {row.value}
                  </span>
                </div>)}
            </div>
            <p className="text-sm text-slate-500 mt-6 font-semibold" data-id="element-5101">
              Je voortgang wordt lokaal in je browser opgeslagen.
            </p>
          </Card>
        </motion.div>
      </div>
    </motion.div>;
}